import { useSelectedEnvironmentKey } from '@gonfalon/app-context/useSelectedEnvironmentKey';
import { isApprovalsEnabled, isExperimentApprovalsEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { Experiment } from '@gonfalon/experiments/types';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toAIConfigTargeting, toFlagTargeting, toHref } from '@gonfalon/navigator/navigators';
import { getFeatureFlagQuery } from '@gonfalon/rest-api/queries/flags';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Alert, AlertText, Button, Link } from '@launchpad-ui/components';
import { useSuspenseQuery } from '@tanstack/react-query';

import { useToggleExperimentFlagOn } from '#components/experimentation/common/hooks/useToggleExperimentFlagOn';

import styles from './TurnOnFlagAlert.module.css';

type TurnOnFlagAlertProps = {
  typeString: string;
  experiment: Experiment | undefined;
  flagKey: string;
};

export function TurnOnFlagAlert({ typeString, experiment, flagKey }: TurnOnFlagAlertProps) {
  const projectKey = useProjectKey();
  const environmentKey = useSelectedEnvironmentKey();
  const { data: flagData } = useSuspenseQuery(
    getFeatureFlagQuery({ projectKey, featureFlagKey: flagKey, query: { env: [environmentKey] } }),
  );

  const isAiConfig = flagData._purpose === 'ai';
  const flagOrConfig = isAiConfig ? 'config' : 'flag';
  const isFlagOff = !flagData.environments?.[environmentKey]?.on;
  const canRequestApproval = isApprovalsEnabled() && isExperimentApprovalsEnabled();

  const { toggleOn, isToggling, hasToggledOn } = useToggleExperimentFlagOn({
    projectKey,
    environmentKey,
    flagKey,
    isAiConfig,
    component: 'TurnOnFlagAlert',
    experiment,
  });

  if (hasToggledOn) {
    return (
      <Alert variant="inline" status="success" className={styles.alert}>
        <Message id="experiments.turnOnFlagAlert.turnedOn" values={{ entity: flagOrConfig, type: typeString }} />
      </Alert>
    );
  }

  if (!isFlagOff) {
    return null;
  }

  const flagTargetingUrl = toHref(
    isAiConfig
      ? toAIConfigTargeting({ projectKey, configKey: flagKey })
      : toFlagTargeting({ projectKey, flagKey, environmentKey }),
  );

  return (
    <Alert status="info" className={styles.alert}>
      <AlertText className={styles.text}>
        <strong>
          <Message id="experiments.turnOnFlagAlert.heading" values={{ type: typeString, entity: flagOrConfig }} />
        </strong>
        <span>
          <Message
            id="experiments.turnOnFlagAlert.body"
            values={{
              entity: flagOrConfig,
              type: typeString,
              canRequestApproval: canRequestApproval ? 'true' : 'false',
              link: (chunks) => <Link href={flagTargetingUrl}>{chunks}</Link>,
              flagName: flagData.name,
            }}
          />
        </span>
      </AlertText>
      <Button variant="default" onPress={toggleOn} isPending={isToggling} isDisabled={isToggling}>
        {msg('experiments.turnOnFlagAlert.toggleOn', { entity: flagOrConfig })}
      </Button>
    </Alert>
  );
}
